import { Transition, Dialog, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { Fragment, useState, useEffect, useContext, useRef } from "react";
import { JsonEditorModal } from "../JsonEditorModal/JsonEditorModal";
import Draggable from "react-draggable";
import { Puzzle, X } from "lucide-react";
import useConfig from "../../demos/hooks/useConfig";
import { CadviewerContext } from "../../context/Cadviewer.Context";

export interface BatchProcessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRefresh?: () => void;
  filePath: string;
  fileName: string;
  jsonFiles?: string[];
}



export const BatchProcessModal = ({
  isOpen,
  onClose,
  onRefresh,
  filePath,
  fileName,
  jsonFiles,
}: BatchProcessModalProps) => {
  const [useInitialization, setUseInitialization] = useState(true);
  const [useUpdate, setUseUpdate] = useState(true);
  const [useExtraction, setUseExtraction] = useState(false);
  const [useCreateDrawing, setUseCreateDrawing] = useState(true);

  // Initialization states
  const [initOutputFilename, setInitOutputFilename] = useState("");
  const [initConfigFilename, setInitConfigFilename] = useState("");
  const [initConfigData, setInitConfigData] = useState("{\n  \n}");

  // Update states
  const [updateOutputFilename, setUpdateOutputFilename] = useState("");
  const [updateConfigFilename, setUpdateConfigFilename] = useState("");
  const [updateConfigData, setUpdateConfigData] = useState("{\n  \n}");

  // Create Drawing states
  const [createDrawingOutputFilename, setCreateDrawingOutputFilename] = useState("");

  const { config } = useConfig();
  let { ServerBackEndUrl } = config;
  const { user } = useContext(CadviewerContext);
  const [loading, setLoading] = useState(false);

  // JSON Editor state
  const [editingConfigType, setEditingConfigType] = useState<"init" | "update" | null>(null);

  const initFileInputRef = useRef<HTMLInputElement>(null);
  const updateFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (fileName) {
      const baseName = fileName.substring(0, fileName.lastIndexOf(".")) || fileName;
      
      setInitOutputFilename(`${baseName}-temp-name.dwg`);
      setInitConfigFilename(`${baseName}-init.json`);
      
      setUpdateOutputFilename(`${baseName}-new.dwg`);
      setUpdateConfigFilename(`${baseName}-changes.json`);
      
      setCreateDrawingOutputFilename(`${baseName}.svg`);
    }
  }, [fileName]);

  const handleLoadJson = async (filename: string, setConfigData: (val: string) => void) => {
    try {
      // Normalize slashes for cross-platform support
      const normalizedPath = filePath.replace(/\\/g, "/");
      const splitedInputPath = normalizedPath.split("/");
      const directory = splitedInputPath.slice(0, -1).join("/");
      const jsonFilePath = directory + "/" + filename;
      
      let endpoint = "loadfile";
      if (!ServerBackEndUrl.endsWith("/")) endpoint = "/" + endpoint;

      const response = await fetch(`${ServerBackEndUrl}${endpoint}?file=${encodeURIComponent(jsonFilePath)}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {})
        }
      });

      if (response.ok) {
        const text = await response.text();
        setConfigData(text);
        alert(`Successfully loaded ${filename}`);
      } else {
        alert(`Failed to load ${filename}`);
      }
    } catch (e) {
      alert(`Error loading ${filename}`);
    }
  };

  const handleSaveJsonToServer = async (filename: string, configData: string) => {
    try {
      let endpoint = "callapivizquery/config-file";
      if (!ServerBackEndUrl.endsWith("/")) endpoint = "/" + endpoint;

      const payload = {
        jsonConfigFilename: filename,
        contentLocation: filePath,
        configData: configData,
      };

      const response = await fetch(`${ServerBackEndUrl}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        alert(`Successfully saved ${filename}`);
        onRefresh?.();
      } else {
        alert(`Failed to save ${filename}`);
      }
    } catch (e) {
      alert(`Error saving ${filename}`);
    }
  };

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    setConfigData: (val: string) => void,
    setConfigFilename: (val: string) => void
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      if (event.target?.result) {
        const content = event.target.result as string;
        setConfigData(content);
        setConfigFilename(file.name);
        // Instantly save/upload it to the server in the DWG folder
        await handleSaveJsonToServer(file.name, content);
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  const handleExecute = async () => {
    try {
      setLoading(true);
      const payload = {
        filename: fileName,
        action: {
          initialization: useInitialization,
          update: useUpdate,
          extraction: useExtraction,
          createDrawing: useCreateDrawing,
        },
        contentLocation: filePath,
        initOutputFilename: initOutputFilename,
        initConfigFilename: initConfigFilename,
        updateOutputFilename: updateOutputFilename,
        updateConfigFilename: updateConfigFilename,
        extractConfigFilename: "",
        extractOutputFilename: "",
        drawingOutputFilename: createDrawingOutputFilename,
      };

      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      if (user?.token) {
        headers.append("Authorization", `Bearer ${user.token}`);
      }

      let endpoint = "callapivizquery";
      if (!ServerBackEndUrl.endsWith("/")) endpoint = "/" + endpoint;

      const response = await fetch(`${ServerBackEndUrl}${endpoint}`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Batch process VizQuery completed successfully:", data);
        alert("Batch process completed successfully.");
        onClose();
      } else {
        const text = await response.text();
        console.error("Batch process failed:", text);
        alert("Batch process failed: " + text);
      }
    } catch (err) {
      console.error("Error executing batch process:", err);
      alert("Error executing batch process.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Transition
        show={isOpen}
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <div className="fixed inset-0 z-[99999999] pointer-events-none flex items-center justify-center">
          <Draggable handle=".modal-header">
            <div className="pointer-events-auto relative w-full max-w-lg bg-white text-left shadow-2xl border border-gray-200 rounded-xl flex flex-col" style={{ maxHeight: "95vh" }}>
              
              {/* Draggable Header */}
              <div className="modal-header bg-white px-6 pt-6 pb-2 cursor-move rounded-t-xl flex items-start justify-between">
                <div className="flex items-start">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-orange-50">
                    <Puzzle className="h-6 w-6 text-orange-500" aria-hidden="true" />
                  </div>
                  <div className="ml-4 mt-1 w-full">
                    <h3 className="text-lg font-semibold leading-6 text-gray-900">
                      Batch process VizQuery - {fileName}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Configure and run VizQuery process on this DWG file.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 ml-4 mt-1"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <X className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="bg-white px-6 pb-6 overflow-y-auto">
                <div className="ml-16">
                  <p className="text-sm font-medium text-gray-700">
                    Source file: <span className="text-orange-500 font-semibold">{fileName}</span>
                  </p>

                  <div className="mt-4 space-y-4 pr-2">
                    {/* Initialization Block */}
                    <div className={`rounded-md border ${useInitialization ? 'border-gray-200 shadow-sm' : 'border-gray-200'} p-4 bg-white`}>
                      <div className="flex items-center">
                        <input
                          id="initialization"
                          name="initialization"
                          type="checkbox"
                          checked={useInitialization}
                          onChange={(e) => setUseInitialization(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                        <label htmlFor="initialization" className="ml-3 block text-sm font-medium text-gray-900">
                          Initialization
                        </label>
                      </div>
                      
                      {useInitialization && (
                        <div className="mt-4 ml-7 space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Output Filename
                            </label>
                            <input
                              type="text"
                              value={initOutputFilename}
                              onChange={(e) => setInitOutputFilename(e.target.value)}
                              className="block w-full rounded-md border-gray-300 px-3 py-2 text-sm border shadow-sm focus:border-orange-500 focus:ring-orange-500 outline-none"
                            />
                          </div>
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <label className="block text-sm font-medium text-gray-700">
                                Configuration Filename
                              </label>
                              <div className="text-sm">
                                <button type="button" onClick={() => setEditingConfigType("init")} className="text-blue-600 hover:underline px-1">View</button> /
                                <button type="button" onClick={() => setEditingConfigType("init")} className="text-green-600 hover:underline px-1">Edit</button> /
                                <button type="button" onClick={() => handleSaveJsonToServer(initConfigFilename, initConfigData)} className="text-red-600 hover:underline px-1">Save</button>
                              </div>
                            </div>
                            <div className="flex rounded-md shadow-sm">
                              <input
                                type="text"
                                value={initConfigFilename}
                                onChange={(e) => setInitConfigFilename(e.target.value)}
                                className="block w-full rounded-none rounded-l-md border-gray-300 px-3 py-2 text-sm border focus:border-orange-500 focus:ring-orange-500 outline-none"
                              />
                              <input
                                type="file"
                                accept=".json"
                                className="hidden"
                                ref={initFileInputRef}
                                onChange={(e) => handleFileUpload(e, setInitConfigData, setInitConfigFilename)}
                              />
                              <button
                                type="button"
                                onClick={() => initFileInputRef.current?.click()}
                                className="relative -ml-px inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50 border border-gray-300 hover:bg-gray-100"
                              >
                                Upload
                              </button>
                              <Menu as="div" className="relative -ml-px block flex-shrink-0">
                                <MenuButton className="relative inline-flex items-center justify-center rounded-r-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white ring-1 ring-inset ring-orange-600 hover:bg-orange-500 cursor-pointer outline-none">
                                  Load ▼
                                </MenuButton>
                                <Transition
                                  as={Fragment}
                                  enter="transition ease-out duration-100"
                                  enterFrom="transform opacity-0 scale-95"
                                  enterTo="transform opacity-100 scale-100"
                                  leave="transition ease-in duration-75"
                                  leaveFrom="transform opacity-100 scale-100"
                                  leaveTo="transform opacity-0 scale-95"
                                >
                                  <MenuItems className="absolute right-0 z-10 mt-1 w-max max-w-[16rem] origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    <div className="py-1">
                                      {jsonFiles?.map((f) => (
                                        <MenuItem key={f}>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setInitConfigFilename(f);
                                              handleLoadJson(f, setInitConfigData);
                                            }}
                                            className="block w-full text-left px-4 py-2 text-sm truncate text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                                            title={f}
                                          >
                                            {f}
                                          </button>
                                        </MenuItem>
                                      ))}
                                      {(!jsonFiles || jsonFiles.length === 0) && (
                                        <div className="px-4 py-2 text-sm text-gray-500">No JSON files found</div>
                                      )}
                                    </div>
                                  </MenuItems>
                                </Transition>
                              </Menu>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Update Block */}
                    <div className={`rounded-md border ${useUpdate ? 'border-gray-200 shadow-sm' : 'border-gray-200'} p-4 bg-white`}>
                      <div className="flex items-center">
                        <input
                          id="update"
                          name="update"
                          type="checkbox"
                          checked={useUpdate}
                          onChange={(e) => setUseUpdate(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                        <label htmlFor="update" className="ml-3 block text-sm font-medium text-gray-900">
                          Update
                        </label>
                      </div>

                      {useUpdate && (
                        <div className="mt-4 ml-7 space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Output Filename
                            </label>
                            <input
                              type="text"
                              value={updateOutputFilename}
                              onChange={(e) => setUpdateOutputFilename(e.target.value)}
                              className="block w-full rounded-md border-gray-300 px-3 py-2 text-sm border shadow-sm focus:border-orange-500 focus:ring-orange-500 outline-none"
                            />
                          </div>
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <label className="block text-sm font-medium text-gray-700">
                                Configuration Filename
                              </label>
                              <div className="text-sm">
                                <button type="button" onClick={() => setEditingConfigType("update")} className="text-blue-600 hover:underline px-1">View</button> /
                                <button type="button" onClick={() => setEditingConfigType("update")} className="text-green-600 hover:underline px-1">Edit</button> /
                                <button type="button" onClick={() => handleSaveJsonToServer(updateConfigFilename, updateConfigData)} className="text-red-600 hover:underline px-1">Save</button>
                              </div>
                            </div>
                            <div className="flex rounded-md shadow-sm">
                              <input
                                type="text"
                                value={updateConfigFilename}
                                onChange={(e) => setUpdateConfigFilename(e.target.value)}
                                className="block w-full rounded-none rounded-l-md border-gray-300 px-3 py-2 text-sm border focus:border-orange-500 focus:ring-orange-500 outline-none"
                              />
                              <input
                                type="file"
                                accept=".json"
                                className="hidden"
                                ref={updateFileInputRef}
                                onChange={(e) => handleFileUpload(e, setUpdateConfigData, setUpdateConfigFilename)}
                              />
                              <button
                                type="button"
                                onClick={() => updateFileInputRef.current?.click()}
                                className="relative -ml-px inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50 border border-gray-300 hover:bg-gray-100"
                              >
                                Upload
                              </button>
                              <Menu as="div" className="relative -ml-px block flex-shrink-0">
                                <MenuButton className="relative inline-flex items-center justify-center rounded-r-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white ring-1 ring-inset ring-orange-600 hover:bg-orange-500 cursor-pointer outline-none">
                                  Load ▼
                                </MenuButton>
                                <Transition
                                  as={Fragment}
                                  enter="transition ease-out duration-100"
                                  enterFrom="transform opacity-0 scale-95"
                                  enterTo="transform opacity-100 scale-100"
                                  leave="transition ease-in duration-75"
                                  leaveFrom="transform opacity-100 scale-100"
                                  leaveTo="transform opacity-0 scale-95"
                                >
                                  <MenuItems className="absolute right-0 z-10 mt-1 w-max max-w-[16rem] origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    <div className="py-1">
                                      {jsonFiles?.map((f) => (
                                        <MenuItem key={f}>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setUpdateConfigFilename(f);
                                              handleLoadJson(f, setUpdateConfigData);
                                            }}
                                            className="block w-full text-left px-4 py-2 text-sm truncate text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                                            title={f}
                                          >
                                            {f}
                                          </button>
                                        </MenuItem>
                                      ))}
                                      {(!jsonFiles || jsonFiles.length === 0) && (
                                        <div className="px-4 py-2 text-sm text-gray-500">No JSON files found</div>
                                      )}
                                    </div>
                                  </MenuItems>
                                </Transition>
                              </Menu>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Extraction Block */}
                    <div className="flex items-center rounded-md border border-gray-200 p-4 bg-white opacity-70">
                      <input
                        id="extraction"
                        name="extraction"
                        type="checkbox"
                        checked={useExtraction}
                        onChange={(e) => setUseExtraction(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-gray-400 focus:ring-orange-500"
                      />
                      <label htmlFor="extraction" className="ml-3 block text-sm font-medium text-gray-500">
                        Extraction
                      </label>
                    </div>

                    {/* Create Drawing Block */}
                    <div className={`rounded-md border ${useCreateDrawing ? 'border-gray-200 shadow-sm' : 'border-gray-200'} p-4 bg-white`}>
                      <div className="flex items-center">
                        <input
                          id="create-drawing"
                          name="create-drawing"
                          type="checkbox"
                          checked={useCreateDrawing}
                          onChange={(e) => setUseCreateDrawing(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                        <label htmlFor="create-drawing" className="ml-3 block text-sm font-medium text-gray-900">
                          Create Drawing
                        </label>
                      </div>
                      
                      {useCreateDrawing && (
                        <div className="mt-4 ml-7 space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Output Filename
                            </label>
                            <input
                              type="text"
                              value={createDrawingOutputFilename}
                              onChange={(e) => setCreateDrawingOutputFilename(e.target.value)}
                              className="block w-full rounded-md border-gray-300 px-3 py-2 text-sm border shadow-sm focus:border-orange-500 focus:ring-orange-500 outline-none"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 flex flex-row-reverse rounded-b-xl border-t border-gray-100 flex-shrink-0">
                <button
                  type="button"
                  className="inline-flex w-full justify-center rounded-md bg-orange-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 sm:ml-3 sm:w-auto"
                  onClick={handleExecute}
                  disabled={loading}
                >
                  {loading ? "Executing..." : "Execute"}
                </button>
                <button
                  type="button"
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  onClick={onClose}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Draggable>
        </div>
      </Transition>

      <JsonEditorModal
        isOpen={editingConfigType !== null}
        onClose={() => setEditingConfigType(null)}
        title={editingConfigType === "init" ? initConfigFilename : updateConfigFilename}
        configData={editingConfigType === "init" ? initConfigData : updateConfigData}
        setConfigData={editingConfigType === "init" ? setInitConfigData : setUpdateConfigData}
        onSave={() => {
          if (editingConfigType === "init") {
            handleSaveJsonToServer(initConfigFilename, initConfigData);
          } else {
            handleSaveJsonToServer(updateConfigFilename, updateConfigData);
          }
        }}
      />
    </>
  );
};
